import { DIR, PALETTE, CRASH_ANIM_TICKS, COUNTDOWN_STEP_MS, SPEED_TICK_MS, SPEED_LABEL, SPEED_COLOR, SPEED_RAMP_CHAIN, SPEED_RAMP_INTERVAL_TICKS, STORAGE_KEY_NAME, STORAGE_KEY_AUTO_START, NAME_PATTERN, MAX_PLAYERS, MAX_SPECTATORS_DISPLAY, UI_COLOR, JUMP_AIRBORNE_TICKS, AFK_THRESHOLD_OPTIONS, AUTO_START_OPTIONS, randomBotName, createGrid, getSpawnPositions } from '@shared/constants.js';
import { botThink } from '@shared/bot.js';
import { getFireballPositions } from '@shared/fireball.js';
import { Direction, ArenaState, Role, ScreenState, Speed } from '@shared/types.js';
import type { ArenaPlayer, ArenaSpectator, ScoreEntry, StartPlayer } from '@shared/types.js';
import { game } from '@client/state.js';
import type { DomEls } from '@client/state.js';
import { sizeCanvas, setCanvasSize, draw } from '@client/renderer.js';
import { setupInput } from '@client/input.js';
import { setupUI, setScreen, updateScoresUI, createBotBadge, createCrownBadge } from '@client/ui.js';
import { connect, send, disconnect, connectStatus, disconnectStatus, hasStoredSession } from '@client/network.js';
import { UnlockId, UNLOCK_REGISTRY, recordSoloResult, getUnlockColor, playFireworks, getSoloStreak, isUnlockActive, getAvailableSpeeds } from '@client/unlocks.js';

declare const __APP_VERSION__: string;
document.getElementById('version')!.textContent = __APP_VERSION__;

// --- Home screen entrance animation ---
// Single sequence definition: [selector, delay (ms), duration (ms), finalOpacity, slideUp (px)]
const HOME_REVEAL_SEQUENCE: Array<[string, number, number, number, number]> = [
  ['#game',           100, 600, 1,   0],
  ['#overlay',        300, 500, 1,   0],
  ['#message',        500, 500, 1,   8],
  ['#name-input',     800, 500, 1,   8],
  ['#online-section', 1000, 500, 1,  8],
  ['#join-arena-btn', 1150, 500, 1,  8],
  ['#online-status',  1250, 500, 0.5, 8],
  ['#local-section',  1350, 500, 1,  8],
  ['#local-play-btn', 1500, 500, 1,  8],
  ['#controls-hint',  1600, 500, 0.4, 8],
];

for (const [selector, delay, duration, finalOpacity, slideUp] of HOME_REVEAL_SEQUENCE) {
  const el = document.querySelector<HTMLElement>(selector);
  if (!el) continue;
  setTimeout(() => {
    el.style.transition = `opacity ${duration}ms ease-out, transform ${duration}ms ease-out`;
    el.style.transform = slideUp ? 'translateY(0)' : '';
    el.style.opacity = String(finalOpacity);
    el.classList.remove('home-hidden');
    // Clean up inline transition after it completes
    setTimeout(() => { el.style.transition = ''; }, duration);
  }, delay);
  // Set initial slide offset for elements that slide up
  if (slideUp) el.style.transform = `translateY(${slideUp}px)`;
}

// --- Render loop (decoupled from game tick) ---
let needsDraw = false;

function renderLoop(): void {
  // Always redraw during gameplay for smooth interpolation
  if (needsDraw || game.state === ScreenState.PLAYING || game.state === ScreenState.SPECTATING || game.state === ScreenState.COUNTDOWN || (game.winnerId !== null && (game.state === ScreenState.GAMEOVER || game.state === ScreenState.ARENA_GAMEOVER))) {
    needsDraw = false;
    draw();
  }
  requestAnimationFrame(renderLoop);
}
requestAnimationFrame(renderLoop);

function schedDraw(): void {
  needsDraw = true;
}

// --- DOM refs ---
const $ = (id: string) => document.getElementById(id)!;
const els: DomEls = {
  header:         $('header'),
  hint:           $('controls-hint'),
  overlay:        $('overlay'),
  message:        $('message'),
  subMessage:     $('sub-message'),
  optionsPanel:   $('options-panel'),
  optionsHomeBtn: $('options-home-btn'),
  playerList:     $('player-list'),
  countDisplay:   $('player-count-display'),
  countMinus:     $('count-minus'),
  countPlus:      $('count-plus'),
  startBtn:       $('start-btn'),
  pauseBtn:       $('pause-btn'),
  joinArenaBtn:   $('join-arena-btn'),
  localPlayBtn:   $('local-play-btn'),
  homeBtn:        $('home-btn'),
  playAgainBtn:   $('play-again-btn'),
  spectateBtn:    $('spectate-btn'),
  arenaPanel:     $('arena-panel'),
  arenaList:      $('arena-list'),
  arenaSpectatorList: $('arena-spectator-list'),
  arenaRoleNotice: $('arena-role-notice'),
  arenaStartRow:  $('arena-start-row'),
  arenaHomeBtn:   $('arena-home-btn'),
  nameInput:      $('name-input') as HTMLInputElement,
  onlineStatus:   $('online-status'),
  dpadArea:       $('dpad-area'),
  speedToggle:    $('speed-toggle'),
  arenaSpeedToggle: $('arena-speed-toggle') as HTMLButtonElement,
  autoStartDisplay: $('auto-start-display'),
  spectatingLabel: $('spectating-label'),
  version:        $('version'),
  arenaActionButtons: $('arena-action-buttons'),
  optionsBtnAction: $('options-btn-action'),
  arenaAutoStartToggle: $('arena-auto-start-toggle') as HTMLButtonElement,
  arenaStartBtn:  $('arena-start-btn') as HTMLButtonElement,
  arenaReadyBtn:  $('arena-ready-btn') as HTMLButtonElement,
  arenaSpectateBtn: $('arena-spectate-btn') as HTMLButtonElement,
  navOverlay:     $('nav-overlay'),
  navResumeBtn:   $('nav-resume-btn'),
  navLobbyBtn:    $('nav-lobby-btn'),
  navHomeBtn:     $('nav-home-btn'),
  navEndGameBtn:  $('nav-end-game-btn'),
};

// --- Name persistence ---
let isFirstHomeLoad = true;
const savedName = localStorage.getItem(STORAGE_KEY_NAME);
if (savedName) {
  els.nameInput.value = savedName;
}

function pulseNameInput(ok: boolean): void {
  const cls = ok ? 'pulse-ok' : 'pulse-err';
  els.nameInput.classList.remove('pulse-ok', 'pulse-err');
  // Force reflow to restart animation
  void els.nameInput.offsetWidth;
  els.nameInput.classList.add(cls);
}

function saveName(): void {
  const name = els.nameInput.value.trim();
  const prev = localStorage.getItem(STORAGE_KEY_NAME) || '';
  // No change — skip pulse entirely
  if (name === prev) return;
  if (!name || !NAME_PATTERN.test(name)) {
    els.nameInput.value = prev;
    pulseNameInput(false);
    return;
  }
  localStorage.setItem(STORAGE_KEY_NAME, name);
  pulseNameInput(true);
}

els.nameInput.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') {
    els.nameInput.blur();
  }
});
els.nameInput.addEventListener('blur', saveName);

// --- Local game ---

function initLocalGame(): void {
  sizeCanvas();
  game.grid = createGrid(game.cols, game.rows);
  game.crashes = [];
  game.crashTick = 0;
  game.pendingWinner = null;
  game.winnerId = null;
  game.fireTrail = {};

  const enteredName = els.nameInput.value.trim();
  // Always rebuild names (player name can change between games)
  game.names = [];
  const humanCount = game.playerIsHuman.slice(0, game.playerCount).filter(Boolean).length;
  let humanIdx = 0;
  for (let i = 0; i < game.playerCount; i++) {
    if (game.playerIsHuman[i]) {
      if (humanCount === 1) {
        game.names[i] = enteredName || 'You';
      } else {
        game.names[i] = enteredName ? `${enteredName} ${++humanIdx}` : `P${++humanIdx}`;
      }
    } else {
      game.names[i] = game._botNames[i] || randomBotName();
    }
  }

  const spawns = getSpawnPositions(game.cols, game.rows, game.playerCount);
  game.players = [];
  game.playerById = {};
  for (let i = 0; i < game.playerCount; i++) {
    const s = spawns[i];
    const p = {
      id: i + 1, x: s.x, y: s.y,
      dir: s.dir, nextDir: s.dir,
      color: PALETTE[i].color, trailColor: PALETTE[i].trail,
      alive: true, isBot: !game.playerIsHuman[i],
      name: game.names[i],
    };
    game.players.push(p);
    game.playerById[p.id] = p;
    if (!(p.id in game.scores)) game.scores[p.id] = 0;
  }
  for (const p of game.players) game.grid[p.y][p.x] = p.id;
  updateScoresUI();
}

function getTickMs(): number {
  return SPEED_TICK_MS[game.speed];
}

function startLocalGame(): void {
  game.online = false;
  initLocalGame();
  game.localDirQueue.length = 0;
  game.prevPositions = {};
  game.speedRampActive = false;
  game.speedRampIndex = 0;
  game.speedRampTickCount = 0;
  game.jumpPending = false;
  game.jumpingTicks = 0;
  game.jumpingPlayerId = null;
  const jumpActive = isUnlockActive(UnlockId.JUMP, game.arenaUnlock);
  game.jumpsLeft = jumpActive ? Infinity : 0;
  // Turbo Speed unlock: override speed to Turbo
  if (isUnlockActive(UnlockId.SPEED_TURBO, game.arenaUnlock)) {
    game.speed = Speed.Turbo;
  }
  draw();
  runCountdown(() => {
    setScreen(ScreenState.PLAYING);
    game.tickMs = getTickMs();
    game.tickTimer = setInterval(localTick, game.tickMs);
  });
}

function runCountdown(onDone: () => void): void {
  const steps = ['3', '2', '1', 'GO'];
  let i = 0;
  setScreen(ScreenState.COUNTDOWN, { msg: steps[i], color: '#0ff' });
  const timer = setInterval(() => {
    i++;
    if (i < steps.length) {
      setScreen(ScreenState.COUNTDOWN, { msg: steps[i], color: i === 3 ? '#0f0' : '#0ff' });
    } else {
      clearInterval(timer);
      onDone();
    }
  }, COUNTDOWN_STEP_MS);
}

function localTick(): void {
  const { players, grid, cols, rows, crashes } = game;
  const botCtx = { grid, cols, rows, players };

  // Speed ramp: step up speed when all humans are dead and only bots remain
  if (game.speedRampActive) {
    game.speedRampTickCount++;
    if (game.speedRampTickCount >= SPEED_RAMP_INTERVAL_TICKS && game.speedRampIndex < SPEED_RAMP_CHAIN.length) {
      const nextSpeed = SPEED_RAMP_CHAIN[game.speedRampIndex];
      const nextTickMs = SPEED_TICK_MS[nextSpeed];
      game.speedRampIndex++;
      game.speedRampTickCount = 0;
      if (game.tickTimer) clearInterval(game.tickTimer);
      game.tickTimer = setInterval(localTick, nextTickMs);
    }
  }

  // Save previous positions for interpolation
  for (const p of players) {
    if (p.alive) game.prevPositions[p.id] = { x: p.x, y: p.y };
  }
  game.lastTickTime = performance.now();

  for (const p of players) if (p.isBot && p.alive && !game.pendingWinner) botThink(p, botCtx);

  // Track fire trail positions before movement (last 3 cells behind player head)
  for (const p of players) {
    if (!p.alive || game.pendingWinner) continue;
    if (!game.fireTrail[p.id]) game.fireTrail[p.id] = [];
    game.fireTrail[p.id].push({ x: p.x, y: p.y });
    if (game.fireTrail[p.id].length > 2) game.fireTrail[p.id].shift();
  }

  for (const p of players) {
    if (!p.alive) continue;
    // Freeze all players once winner is determined (let crash animation play)
    if (game.pendingWinner) continue;
    // Drain one buffered turn for the local human (bots set nextDir via botThink).
    if (!p.isBot && game.localDirQueue.length > 0) p.nextDir = game.localDirQueue.shift()!;
    p.dir = p.nextDir;
    const d = DIR[p.dir];

    // Jump: spans 2 ticks. Tick 1 = airborne (skip collision), Tick 2 = land (collision check).
    if (!p.isBot && game.jumpingTicks > 0 && game.jumpingPlayerId === p.id) {
      game.jumpingTicks--;
      p.x += d.x;
      p.y += d.y;
      if (game.jumpingTicks > 0) {
        continue; // airborne — no trail, no collision
      }
      continue; // landing — skip normal movement, collision check below handles it
    } else if (!p.isBot && game.jumpPending && game.jumpsLeft > 0) {
      // Initiate jump: mark trail at current pos, start 2-tick jump
      grid[p.y][p.x] = p.id;
      game.jumpingTicks = JUMP_AIRBORNE_TICKS;
      game.jumpingPlayerId = p.id;
      game.jumpsLeft--;
      game.jumpPending = false;
      // Move 1 cell (tick 1 of jump — airborne, skip collision)
      p.x += d.x;
      p.y += d.y;
      continue;
    }

    p.x += d.x;
    p.y += d.y;
  }
  game.jumpPending = false;

  for (const p of players) {
    if (!p.alive) continue;
    // Skip collision for airborne (jumping) player
    if (game.jumpingTicks > 0 && game.jumpingPlayerId === p.id) continue;
    if (p.x < 0 || p.x >= cols || p.y < 0 || p.y >= rows || grid[p.y][p.x] !== 0) {
      p.alive = false;
      const cx = Math.max(0, Math.min(cols - 1, p.x));
      const cy = Math.max(0, Math.min(rows - 1, p.y));
      crashes.push({ x: cx, y: cy, color: p.color, tick: game.crashTick });
      // Block the crash cell so no one can pass through the wreckage
      if (grid[cy][cx] === 0) grid[cy][cx] = p.id;
    }
  }

  const alive = players.filter(p => p.alive);
  for (let i = 0; i < alive.length; i++) {
    for (let j = i + 1; j < alive.length; j++) {
      if (alive[i].x === alive[j].x && alive[i].y === alive[j].y) {
        alive[i].alive = false;
        alive[j].alive = false;
        crashes.push({ x: alive[i].x, y: alive[i].y, color: '#fff', tick: game.crashTick });
        if (grid[alive[i].y][alive[i].x] === 0) grid[alive[i].y][alive[i].x] = -1;
      }
    }
  }

  // Fireball aura collision — kill players who enter fireball orbit cells
  const auraActive = isUnlockActive(UnlockId.FIREBALL_AURA, game.arenaUnlock);
  if (auraActive) {
    const auraPlayers = players.filter(p => p.alive && !p.isBot);
    for (const ap of auraPlayers) {
      const fireballs = getFireballPositions(ap.x, ap.y, game.crashTick);
      for (const fb of fireballs) {
        for (const victim of players) {
          if (!victim.alive || victim.id === ap.id) continue;
          if (victim.x === fb.x && victim.y === fb.y) {
            victim.alive = false;
            crashes.push({ x: victim.x, y: victim.y, color: victim.color, tick: game.crashTick });
          }
        }
      }
    }
  }

  const stillAlive = players.filter(p => p.alive);
  for (const p of stillAlive) {
    // Don't write trail for airborne jumping player
    if (game.jumpingTicks > 0 && game.jumpingPlayerId === p.id) continue;
    grid[p.y][p.x] = p.id;
  }
  game.crashTick++;
  schedDraw();

  // Record winner at the moment only one player remains
  if (!game.pendingWinner && stillAlive.length === 1) {
    game.pendingWinner = stillAlive[0];
    game.winnerId = stillAlive[0].id;
  }

  // Auto speed ramp: when all humans die and only bots remain alive
  if (!game.speedRampActive && stillAlive.length > 1 && stillAlive.every(p => p.isBot)) {
    const tickMs = SPEED_TICK_MS[game.speed];
    let startIdx = SPEED_RAMP_CHAIN.findIndex(s => SPEED_TICK_MS[s] <= tickMs);
    if (startIdx === -1) startIdx = 0;
    else startIdx = Math.min(startIdx + 1, SPEED_RAMP_CHAIN.length);
    game.speedRampActive = true;
    game.speedRampIndex = startIdx;
    game.speedRampTickCount = 0;
  }

  if (stillAlive.length <= 1) {
    const lastCrash = crashes.length > 0 ? crashes[crashes.length - 1] : null;
    if (lastCrash && game.crashTick - lastCrash.tick < CRASH_ANIM_TICKS) return;

    const isSolo = !game.online && game.playerIsHuman.slice(0, game.playerCount).filter(Boolean).length === 1;

    if (game.pendingWinner) {
      const w = game.pendingWinner;
      game.scores[w.id] = (game.scores[w.id] || 0) + 1;
      updateScoresUI();

      const humanWon = isSolo && !w.isBot;
      const unlock = isSolo ? recordSoloResult(humanWon) : null;

      if (unlock) {
        const unlockColor = getUnlockColor(unlock);
        setScreen(ScreenState.GAMEOVER, { msg: unlock.title, sub: unlock.subtitle, color: unlockColor });
        playFireworks(unlockColor);
      } else {
        const streak = getSoloStreak();
        const winMsg = !w.isBot ? 'You WIN' : w.name + ' WINS';
        const streakSub = isSolo && humanWon && streak > 0 ? `Streak: ${streak}x` : undefined;
        setScreen(ScreenState.GAMEOVER, { msg: winMsg, sub: streakSub, color: w.color });
      }
    } else {
      if (isSolo) recordSoloResult(false);
      setScreen(ScreenState.GAMEOVER, { msg: 'DRAW', color: '#fff' });
    }
    game.pendingWinner = null;
  }
}

// --- Online mode ---

function loadGameState(msg: { cols: number; rows: number; players: StartPlayer[]; scores?: Record<string, ScoreEntry> }): void {
  setCanvasSize(msg.cols, msg.rows);

  game.grid = createGrid(game.cols, game.rows);
  game.crashes = [];
  game.crashTick = 0;
  game.winnerId = null;

  game.players = msg.players.map(p => ({ ...p, nextDir: p.dir }));
  game.playerById = {};
  for (const p of game.players) {
    game.playerById[p.id] = p;
    game.grid[p.y][p.x] = p.id;
  }

  game.scores = {};
  const scoreData = msg.scores;
  if (scoreData) {
    for (const [id, entry] of Object.entries(scoreData)) {
      game.scores[Number(id)] = entry.score;
    }
  }
  // Ensure all current players have a score entry
  for (const p of game.players) {
    if (!(p.id in game.scores)) game.scores[p.id] = 0;
  }
  updateScoresUI();
}

// Delay before the JOIN ARENA flow falls back to a fresh join. With a stored
// session we wait longer so a token reconnect can reclaim the slot first (#38);
// with no session we join almost immediately.
const FRESH_JOIN_MS = 300;
const RECONNECT_FALLBACK_JOIN_MS = 1500;

function goOnline(): void {
  game.online = true;
  game.onlineRole = null;
  game.promotedFromSpectator = false;
  disconnectStatus();

  saveName();
  const name = els.nameInput.value.trim() || 'Player';
  let joinTimer: ReturnType<typeof setTimeout> | null = null;

  connect({
    onWelcome(msg) {
      if (msg.roomState === ArenaState.PLAYING) {
        // Will get sync after join
      }
    },
    onJoined(playerId) {
      game.onlinePlayerId = playerId;
      // We're in — cancel any pending fallback join.
      if (joinTimer) { clearTimeout(joinTimer); joinTimer = null; }
      // Role will be set when we get the arena or sync message
    },
    onReconnected(playerId, role) {
      game.onlinePlayerId = playerId;
      game.onlineRole = role;
      // Reconnect reclaimed the slot — cancel the fallback join.
      if (joinTimer) { clearTimeout(joinTimer); joinTimer = null; }
    },
    onArena(msg) {
      // Update scores from server
      game.scores = {};
      for (const [id, entry] of Object.entries(msg.scores)) {
        game.scores[Number(id)] = entry.score;
      }

      game.arenaSpeed = msg.speed;
      game.arenaAutoStart = msg.autoStart;
      game.arenaUnlock = msg.unlock;
      game.arenaAfkThreshold = msg.afkThreshold;
      arenaAfkToggle.textContent = afkThresholdLabel(msg.afkThreshold);
      game.arenaAutoStartSeconds = msg.autoStartSeconds;
      arenaAutoStartSecsToggle.textContent = autoStartSecsLabel(msg.autoStartSeconds);
      els.arenaSpeedToggle.textContent = SPEED_LABEL[msg.speed];
      els.arenaSpeedToggle.style.color = SPEED_COLOR[msg.speed];
      els.arenaSpeedToggle.style.borderColor = SPEED_COLOR[msg.speed];
      // Update arena unlock toggle
      const unlockDef = msg.unlock ? UNLOCK_REGISTRY.find(d => d.id === msg.unlock) : null;
      arenaUnlockToggle.textContent = unlockDef ? unlockDef.title : 'NONE';
      const unlockColor = unlockDef ? getUnlockColor(unlockDef) : '#666';
      arenaUnlockToggle.style.color = unlockColor;
      arenaUnlockToggle.style.borderColor = unlockColor;
      arenaUnlockIdx = ARENA_UNLOCK_OPTIONS.indexOf(msg.unlock);
      if (arenaUnlockIdx === -1) arenaUnlockIdx = 0;
      const wasHost = game.isHost;
      game.isHost = msg.hostId === game.onlinePlayerId;

      // New host: send saved auto-start preference (default: off)
      if (game.isHost && !wasHost) {
        const savedRaw = localStorage.getItem(STORAGE_KEY_AUTO_START);
        const saved = savedRaw === null ? false : savedRaw === 'true';
        if (saved !== msg.autoStart) {
          send({ type: 'setAutoStart', enabled: saved });
        }
      }

      // Auto-transition to arena from reconnecting/gameover states
      if (game.state === ScreenState.RECONNECTING || game.state === ScreenState.ARENA_GAMEOVER) {
        setScreen(ScreenState.ARENA);
      } else if (game.state !== ScreenState.PLAYING && game.state !== ScreenState.COUNTDOWN && game.state !== ScreenState.SPECTATING) {
        setScreen(ScreenState.ARENA);
      }

      // Set role before rendering: host or listed player → PLAYER, otherwise SPECTATOR
      const isLoading = msg.players.length === 0 && game.onlinePlayerId === null;
      if (!isLoading) {
        const me = msg.players.find(p => p.id === game.onlinePlayerId);
        game.onlineRole = (msg.hostId === game.onlinePlayerId || me) ? Role.PLAYER : Role.SPECTATOR;
        // Back to spectating (e.g. voluntary spectate) — the seat notice no longer applies
        if (game.onlineRole === Role.SPECTATOR) {
          game.promotedFromSpectator = false;
        }
      }

      // Render after setScreen so buttons aren't hidden by reset
      renderArena(msg.players, msg.spectators, msg.hostId, msg.autoStart, msg.autoStartIn);
    },
    onYoureIn(msg) {
      // Server seated us as a competitor — surface it if we were spectating
      if (game.onlineRole === Role.SPECTATOR) {
        game.promotedFromSpectator = true;
      }
      game.onlinePlayerId = msg.playerId;
      game.onlineRole = Role.PLAYER;
    },
    onAutoStart(msg) {
      els.autoStartDisplay.textContent = `Starting in ${msg.seconds}...`;
      els.autoStartDisplay.style.visibility = 'visible';
    },
    onGameStart(msg) {
      loadGameState(msg);
      game.tickMs = msg.tickMs;
      game.prevPositions = {};
      game.fireTrail = {};
      game.promotedFromSpectator = false;
      els.autoStartDisplay.style.visibility = 'hidden';

      // Check if we're a player or spectator
      const isPlayer = msg.players.some(p => p.id === game.onlinePlayerId);
      game.onlineRole = isPlayer ? Role.PLAYER : Role.SPECTATOR;

      draw();
      if (game.onlineRole === Role.PLAYER) {
        runCountdown(() => {
          setScreen(ScreenState.PLAYING);
        });
      } else {
        runCountdown(() => {
          setScreen(ScreenState.SPECTATING);
        });
      }
    },
    onSync(msg) {
      setCanvasSize(msg.cols, msg.rows);
      game.grid = msg.grid;
      game.crashes = msg.crashes;
      game.crashTick = msg.crashTick;
      game.players = msg.players.map(p => ({ ...p, nextDir: p.dir }));
      game.playerById = {};
      for (const p of game.players) game.playerById[p.id] = p;
      game.scores = {};
      if (msg.scores) {
        for (const [id, entry] of Object.entries(msg.scores)) {
          game.scores[Number(id)] = entry.score;
        }
      }
      for (const p of game.players) {
        if (!(p.id in game.scores)) game.scores[p.id] = 0;
      }
      updateScoresUI();
      draw();

      // Determine role
      const isPlayer = msg.players.some(p => p.id === game.onlinePlayerId);
      game.onlineRole = isPlayer ? Role.PLAYER : Role.SPECTATOR;

      if (game.onlineRole === Role.PLAYER) {
        setScreen(ScreenState.PLAYING);
      } else if (game.state !== ScreenState.SPECTATING) {
        // Fresh spectate entry (joined mid-game) — explain the state before showing the game
        setScreen(ScreenState.SPECTATING, { msg: 'GAME IN PROGRESS', sub: "You're spectating until this game ends", color: UI_COLOR.WARNING });
      } else {
        setScreen(ScreenState.SPECTATING);
      }
    },
    onTick(msg) {
      // Set jump state from server (authoritative)
      if (msg.jumping.length > 0) {
        game.jumpingPlayerId = msg.jumping[0];
        game.jumpingTicks = 1;
      } else {
        game.jumpingTicks = 0;
        game.jumpingPlayerId = null;
      }
      // Save previous positions for interpolation + fire trail
      for (const update of msg.players) {
        const p = game.playerById[update.id];
        if (p && p.alive) {
          game.prevPositions[p.id] = { x: p.x, y: p.y };
          if (!game.fireTrail[p.id]) game.fireTrail[p.id] = [];
          game.fireTrail[p.id].push({ x: p.x, y: p.y });
          if (game.fireTrail[p.id].length > 2) game.fireTrail[p.id].shift();
        }
      }
      game.lastTickTime = performance.now();

      for (const update of msg.players) {
        const p = game.playerById[update.id];
        if (p) {
          p.x = update.x; p.y = update.y;
          p.dir = update.dir; p.alive = update.alive;
        }
      }
      for (const t of msg.newTrails) {
        game.grid[t.y][t.x] = t.id;
      }
      for (const c of msg.newCrashes) {
        game.crashes.push(c);
      }
      game.crashTick = msg.crashTick;
      // Early winner detection: if only one player alive, set winnerId now
      // (before gameover message arrives) so the taunt renders immediately
      if (game.winnerId === null) {
        const aliveNow = game.players.filter(p => p.alive);
        if (aliveNow.length === 1) {
          game.winnerId = aliveNow[0].id;
        }
      }
      schedDraw();
    },
    onGameOver(msg) {
      // Server sends the authoritative winnerId — no display-string parsing.
      game.winnerId = msg.winnerId;
      const displayMsg = msg.winnerId !== null && msg.winnerId === game.onlinePlayerId
        ? 'You WIN'
        : msg.msg;
      setScreen(ScreenState.ARENA_GAMEOVER, { msg: displayMsg, color: msg.color });
    },
    onStatus(msg) {
      updateOnlineStatus(msg.online, msg.gameInProgress);
    },
    onError(msg) {
      console.error('Server:', msg);
      // An error while we're mid-reconnect/rejoin means recovery itself failed
      // (e.g. the rejoin's name was rejected) — fall back home rather than
      // stranding the player on the RECONNECTING screen.
      if (game.state === ScreenState.RECONNECTING) backToHome();
    },
    onDisconnect() {
      // Connection lost — reconnect will be attempted automatically
    },
    onReconnecting() {
      setScreen(ScreenState.RECONNECTING, { sub: 'Bot is covering for you...' });
    },
    onReconnectFailed() {
      // Session is gone (server restarted). The socket is open — rejoin the
      // arena fresh instead of hanging on RECONNECTING. onJoined/onArena will
      // transition back to the arena screen.
      setScreen(ScreenState.RECONNECTING, { sub: 'Rejoining…' });
      send({ type: 'join', name });
    },
  });

  // With a stored session (e.g. a page refresh), give the token reconnect time
  // to reclaim the existing slot before falling back to a fresh join — racing it
  // with an immediate join would create a duplicate while the old slot lingers
  // as a ghost host (#38). onReconnected/onJoined cancel this fallback; it only
  // fires if the reconnect stalls (server never replies on the open socket), so
  // the player is never stranded on the arena skeleton. No session → join promptly.
  const joinDelayMs = hasStoredSession() ? RECONNECT_FALLBACK_JOIN_MS : FRESH_JOIN_MS;
  joinTimer = setTimeout(() => {
    joinTimer = null;
    send({ type: 'join', name });
  }, joinDelayMs);
  setScreen(ScreenState.ARENA);
  // Render skeleton immediately to prevent layout jump when server responds
  renderArena([], [], null, true, null);
}

function renderArena(players: ArenaPlayer[], spectators: ArenaSpectator[], hostId: number | null, autoStart: boolean, autoStartIn: number | null): void {
  const isLoading = players.length === 0 && game.onlinePlayerId === null;
  const isHost = !isLoading && hostId !== null && hostId === game.onlinePlayerId;

  els.arenaRoleNotice.style.visibility = game.promotedFromSpectator ? 'visible' : 'hidden';

  // Always render MAX_PLAYERS rows (skeleton for empty slots)
  els.arenaList.innerHTML = '';
  for (let i = 0; i < MAX_PLAYERS; i++) {
    const p = players[i];
    const div = document.createElement('div');
    div.className = 'opt-row' + (p ? '' : ' arena-slot-empty');

    const label = document.createElement('span');
    label.className = 'opt-label';
    const dot = document.createElement('span');
    dot.className = 'color-dot';
    dot.style.background = p ? (p.color || PALETTE[i]?.color || UI_COLOR.FALLBACK) : '#333';
    label.appendChild(dot);

    if (p) {
      const isYou = p.id === game.onlinePlayerId;
      const isPlayerHost = p.id === hostId;

      const youSuffix = isYou ? ' (YOU)' : '';
      label.appendChild(document.createTextNode(p.name + youSuffix));
      if (p.isBot) {
        label.appendChild(createBotBadge());
      }

      if (isPlayerHost) {
        label.appendChild(createCrownBadge());
      }

      div.appendChild(label);

      // Right side: status + kick button
      const rightSide = document.createElement('span');
      rightSide.style.display = 'flex'; /* cls-ok: initial style on new element */
      rightSide.style.alignItems = 'center';

      // Score badge
      const playerScore = game.scores[p.id];
      if (playerScore && playerScore > 0) {
        const scoreBadge = document.createElement('span');
        scoreBadge.className = 'arena-score';
        scoreBadge.textContent = String(playerScore);
        scoreBadge.style.color = p.color || PALETTE[i]?.color || UI_COLOR.FALLBACK;
        rightSide.appendChild(scoreBadge);
      }

      const status = document.createElement('span');
      status.className = 'arena-status';
      if (p.ready) {
        status.textContent = 'READY';
        status.style.color = UI_COLOR.READY;
      } else {
        status.textContent = 'NOT READY';
        status.style.color = UI_COLOR.MUTED;
      }
      rightSide.appendChild(status);

      // Kick button: host-only, can't kick yourself
      if (isHost && p.id !== game.onlinePlayerId) {
        const kickBtn = document.createElement('button');
        kickBtn.className = 'arena-kick-btn';
        kickBtn.textContent = '✕';
        kickBtn.title = p.isBot ? 'Remove bot' : 'Move to spectators';
        kickBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          send({ type: 'kick', playerId: p.id });
        });
        rightSide.appendChild(kickBtn);
      }

      div.appendChild(rightSide);
    } else {
      // Empty slot
      label.appendChild(document.createTextNode('—'));
      div.appendChild(label);

      // BOT + button for host in empty slots
      if (isHost) {
        const botBtn = document.createElement('button');
        botBtn.className = 'arena-slot-bot-btn';
        botBtn.textContent = '+ BOT';
        botBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          send({ type: 'addBot' });
        });
        div.appendChild(botBtn);
      }
    }

    els.arenaList.appendChild(div);
  }

  // Spectator list (3-column grid, capped, fixed height for CLS)
  els.arenaSpectatorList.innerHTML = '';
  const cappedSpectators = spectators.slice(0, MAX_SPECTATORS_DISPLAY);
  if (cappedSpectators.length > 0) {
    for (const s of cappedSpectators) {
      const span = document.createElement('span');
      span.className = 'arena-spectator-item';
      const isYou = s.id === game.onlinePlayerId;
      span.textContent = s.name;
      if (s.afk) {
        const afkTag = document.createElement('span');
        afkTag.className = 'arena-spectator-afk';
        afkTag.textContent = 'AFK';
        span.appendChild(afkTag);
      }
      if (s.queuePos !== null) {
        const queueTag = document.createElement('span');
        queueTag.className = 'arena-spectator-queue';
        queueTag.textContent = `#${s.queuePos}`;
        queueTag.title = 'Waiting for a seat';
        span.appendChild(queueTag);
      }
      if (isYou) {
        span.appendChild(document.createTextNode(' (YOU)'));
      }
      els.arenaSpectatorList.appendChild(span);
    }
  } else {
    const none = document.createElement('span');
    none.className = 'arena-spectator-item';
    none.style.fontStyle = 'italic';
    none.textContent = 'None';
    els.arenaSpectatorList.appendChild(none);
  }

  // Speed toggle: only host can change
  els.arenaSpeedToggle.disabled = !isHost;
  els.arenaSpeedToggle.style.opacity = isHost ? '' : '0.5';

  // Unlock toggle: visible for host, hidden for others
  arenaUnlockRow.style.display = isHost ? 'flex' : 'none'; /* cls-ok: toggled by screen transition */
  (arenaUnlockToggle as HTMLButtonElement).disabled = !isHost;

  // AFK-kick threshold toggle: visible for host, hidden for others
  arenaAfkRow.style.display = isHost ? 'flex' : 'none'; /* cls-ok: toggled by screen transition */
  (arenaAfkToggle as HTMLButtonElement).disabled = !isHost;

  // Auto-start countdown toggle: host-only, and only meaningful when auto-start is on.
  arenaAutoStartSecsRow.style.display = isHost && autoStart ? 'flex' : 'none'; /* cls-ok: toggled by screen transition */
  (arenaAutoStartSecsToggle as HTMLButtonElement).disabled = !isHost;

  // Auto-start countdown display
  if (autoStartIn !== null) {
    els.autoStartDisplay.textContent = `Starting in ${autoStartIn}...`;
    els.autoStartDisplay.style.visibility = 'visible';
  } else {
    els.autoStartDisplay.style.visibility = 'hidden';
  }

  // Start row: always visible, buttons vary by role
  els.arenaStartRow.style.display = ''; /* cls-ok: shown during screen transition, min-height reserved */
  const me = players.find(p => p.id === game.onlinePlayerId);

  if (isLoading) {
    // Loading state: show skeleton buttons with pulse
    els.arenaStartBtn.classList.add('hidden');
    els.arenaAutoStartToggle.classList.add('hidden');
    els.arenaReadyBtn.classList.remove('hidden');
    els.arenaSpectateBtn.classList.remove('hidden');
    els.arenaReadyBtn.textContent = '\u00A0';
    els.arenaSpectateBtn.textContent = '\u00A0';
    els.arenaReadyBtn.style.borderColor = '#333';
    els.arenaReadyBtn.style.color = '#333';
    els.arenaSpectateBtn.style.borderColor = '#222';
    els.arenaSpectateBtn.style.color = '#222';
    els.arenaReadyBtn.classList.add('skeleton-pulse');
    els.arenaSpectateBtn.classList.add('skeleton-pulse');
    els.arenaReadyBtn.disabled = true;
    els.arenaSpectateBtn.disabled = true;
  } else if (isHost) {
    // Host: START GAME + AUTO, no READY/SPECTATE (host is always ready)
    els.arenaReadyBtn.classList.remove('skeleton-pulse');
    els.arenaSpectateBtn.classList.remove('skeleton-pulse');
    els.arenaSpectateBtn.disabled = false;
    els.arenaStartBtn.classList.remove('hidden');
    els.arenaAutoStartToggle.classList.remove('hidden');
    els.arenaReadyBtn.classList.add('hidden');
    els.arenaSpectateBtn.classList.add('hidden');

    const totalReady = players.filter(p => p.ready).length;
    els.arenaStartBtn.disabled = totalReady < 2;
    els.arenaStartBtn.style.opacity = totalReady < 2 ? '0.3' : '';

    els.arenaAutoStartToggle.textContent = autoStart ? 'AUTO ✓' : 'AUTO';
    els.arenaAutoStartToggle.style.color = autoStart ? UI_COLOR.READY : UI_COLOR.MUTED;
    els.arenaAutoStartToggle.style.borderColor = autoStart ? UI_COLOR.READY : UI_COLOR.MUTED;
  } else if (me) {
    // Non-host player: READY + SPECTATE
    els.arenaReadyBtn.classList.remove('skeleton-pulse');
    els.arenaSpectateBtn.classList.remove('skeleton-pulse');
    els.arenaSpectateBtn.disabled = false;
    els.arenaSpectateBtn.textContent = 'SPECTATE';
    els.arenaSpectateBtn.style.borderColor = '#666';
    els.arenaSpectateBtn.style.color = '#666';
    els.arenaStartBtn.classList.add('hidden');
    els.arenaAutoStartToggle.classList.add('hidden');
    els.arenaReadyBtn.classList.remove('hidden');
    els.arenaSpectateBtn.classList.remove('hidden');

    if (me.ready) {
      els.arenaReadyBtn.textContent = 'READY ✓';
      els.arenaReadyBtn.style.borderColor = UI_COLOR.READY;
      els.arenaReadyBtn.style.color = UI_COLOR.READY;
      els.arenaReadyBtn.classList.remove('pulse-ready');
      els.arenaReadyBtn.disabled = true;
      els.arenaReadyBtn.style.cursor = 'default';
      els.arenaReadyBtn.style.opacity = '1';
    } else {
      els.arenaReadyBtn.textContent = 'READY';
      els.arenaReadyBtn.disabled = false;
      els.arenaReadyBtn.style.cursor = '';
      els.arenaReadyBtn.style.borderColor = UI_COLOR.PRIMARY;
      els.arenaReadyBtn.style.color = UI_COLOR.PRIMARY;
      els.arenaReadyBtn.classList.add('pulse-ready');
    }
  } else {
    // Spectator: COMPETE is always actionable. With seats open it seats you;
    // when full it joins the seat queue (no silent no-op), and the button
    // reflects your queue position until a seat opens.
    els.arenaReadyBtn.classList.remove('skeleton-pulse');
    els.arenaSpectateBtn.classList.remove('skeleton-pulse');
    els.arenaStartBtn.classList.add('hidden');
    els.arenaAutoStartToggle.classList.add('hidden');
    els.arenaSpectateBtn.classList.add('hidden');

    const meSpec = spectators.find(s => s.id === game.onlinePlayerId);
    const queuePos = meSpec?.queuePos ?? null;
    els.arenaReadyBtn.classList.remove('hidden');
    els.arenaReadyBtn.disabled = false;
    els.arenaReadyBtn.style.cursor = '';
    if (queuePos !== null) {
      els.arenaReadyBtn.textContent = `IN QUEUE — #${queuePos}`;
      els.arenaReadyBtn.style.borderColor = UI_COLOR.READY;
      els.arenaReadyBtn.style.color = UI_COLOR.READY;
      els.arenaReadyBtn.classList.remove('pulse-ready');
    } else {
      els.arenaReadyBtn.textContent = 'COMPETE';
      els.arenaReadyBtn.style.borderColor = UI_COLOR.PRIMARY;
      els.arenaReadyBtn.style.color = UI_COLOR.PRIMARY;
      els.arenaReadyBtn.classList.add('pulse-ready');
    }
  }
}

function updateOnlineStatus(online: number, gameInProgress: boolean): void {
  const parts: string[] = [`${online} online`];
  if (gameInProgress) parts.push('game in progress');
  els.onlineStatus.textContent = parts.join(' · ');
}

function showNavOverlay(): void {
  // Show End Game button only for host when only bots are alive
  const onlyBotsAlive = game.isHost &&
    game.players.filter(p => p.alive && !p.isBot).length === 0 &&
    game.players.some(p => p.alive);
  els.navEndGameBtn.style.visibility = onlyBotsAlive ? 'visible' : 'hidden';

  // Persistent overlay: the countdown/game keeps running underneath; it stays
  // up until the player picks Resume / Back to Lobby / Home or presses Esc (#28).
  els.navOverlay.classList.add('active');
}

function hideNavOverlay(): void {
  els.navOverlay.classList.remove('active');
}

function toggleNavOverlay(): void {
  if (els.navOverlay.classList.contains('active')) {
    hideNavOverlay();
  } else {
    showNavOverlay();
  }
}

function backToHome(): void {
  if (game.online) {
    send({ type: 'leave' });
    disconnect();
    game.online = false;
    game.onlineRole = null;
    game.isHost = false;
  }
  sizeCanvas();
  initLocalGame();
  draw();
  setScreen(ScreenState.HOME);
  connectStatus(updateOnlineStatus);
}

// --- Wire up nav overlay buttons ---
els.navResumeBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  hideNavOverlay();
});
els.navLobbyBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  hideNavOverlay();
  send({ type: 'leave' });
  disconnect();
  goOnline();
});
els.navHomeBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  hideNavOverlay();
  backToHome();
});
els.navEndGameBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  hideNavOverlay();
  send({ type: 'endBotGame' });
});

// --- Wire up arena buttons ---
els.arenaHomeBtn.addEventListener('click', (e) => { e.stopPropagation(); backToHome(); });
// Arena AFK-kick threshold toggle — host cycles Off / 2 / 3 / 5 games
const arenaAfkToggle = document.getElementById('arena-afk-toggle')!;
const arenaAfkRow = document.getElementById('arena-afk-row')!;
function afkThresholdLabel(threshold: number): string {
  return threshold === 0 ? 'OFF' : `${threshold} GAMES`;
}
arenaAfkToggle.addEventListener('click', (e) => {
  e.stopPropagation();
  const idx = AFK_THRESHOLD_OPTIONS.indexOf(game.arenaAfkThreshold);
  const next = AFK_THRESHOLD_OPTIONS[(idx + 1) % AFK_THRESHOLD_OPTIONS.length];
  send({ type: 'setAfkThreshold', threshold: next });
});

// Arena auto-start countdown toggle — host cycles Auto / 2s / 3s / 5s
const arenaAutoStartSecsToggle = document.getElementById('arena-autostart-secs-toggle')!;
const arenaAutoStartSecsRow = document.getElementById('arena-autostart-secs-row')!;
function autoStartSecsLabel(seconds: number): string {
  return seconds === 0 ? 'AUTO' : `${seconds}s`;
}
arenaAutoStartSecsToggle.addEventListener('click', (e) => {
  e.stopPropagation();
  const idx = AUTO_START_OPTIONS.indexOf(game.arenaAutoStartSeconds);
  const next = AUTO_START_OPTIONS[(idx + 1) % AUTO_START_OPTIONS.length];
  send({ type: 'setAutoStartSeconds', seconds: next });
});

// Arena unlock toggle — host cycles through unlocks
const arenaUnlockToggle = document.getElementById('arena-unlock-toggle')!;
const arenaUnlockRow = document.getElementById('arena-unlock-row')!;
const ARENA_UNLOCK_OPTIONS: Array<UnlockId | null> = [null, ...UNLOCK_REGISTRY.filter(d => !d.disabled).map(d => d.id)];
let arenaUnlockIdx = 0;
arenaUnlockToggle.addEventListener('click', (e) => {
  e.stopPropagation();
  arenaUnlockIdx = (arenaUnlockIdx + 1) % ARENA_UNLOCK_OPTIONS.length;
  const unlock = ARENA_UNLOCK_OPTIONS[arenaUnlockIdx];
  send({ type: 'setUnlock', unlock });
});

els.arenaSpeedToggle.addEventListener('click', (e) => {
  e.stopPropagation();
  const speeds = getAvailableSpeeds();
  const idx = speeds.indexOf(game.arenaSpeed);
  const next = speeds[(idx + 1) % speeds.length];
  send({ type: 'setSpeed', speed: next });
});
els.arenaAutoStartToggle.addEventListener('click', (e) => {
  e.stopPropagation();
  const enabled = !game.arenaAutoStart;
  send({ type: 'setAutoStart', enabled });
  localStorage.setItem(STORAGE_KEY_AUTO_START, String(enabled));
});
els.arenaStartBtn.addEventListener('click', (e) => { e.stopPropagation(); send({ type: 'startGame' }); });
els.arenaReadyBtn.addEventListener('click', (e) => { e.stopPropagation(); send({ type: 'ready' }); });
els.arenaSpectateBtn.addEventListener('click', (e) => { e.stopPropagation(); send({ type: 'spectate' }); });
els.joinArenaBtn.addEventListener('click', (e) => { e.stopPropagation(); goOnline(); });
els.localPlayBtn.addEventListener('click', (e) => { e.stopPropagation(); setScreen(ScreenState.LOCAL_SETUP); });
els.homeBtn.addEventListener('click', (e) => { e.stopPropagation(); backToHome(); });
els.optionsHomeBtn.addEventListener('click', (e) => { e.stopPropagation(); backToHome(); });
els.playAgainBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  if (game.online) {
    send({ type: 'ready' });
  } else {
    startLocalGame();
  }
});
els.spectateBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  send({ type: 'spectate' });
});

// --- Wire up input & UI ---
setupUI(els, startLocalGame);
setupInput(els, {
  onPause() {
    if (game.online) return;
    setScreen(ScreenState.PAUSED);
  },
  onResume() {
    if (game.online) return;
    game.lastTickTime = 0;
    setScreen(ScreenState.PLAYING);
    game.tickTimer = setInterval(localTick, getTickMs());
  },
  onStart() {
    if (game.online) {
      if (game.state === ScreenState.ARENA_GAMEOVER) {
        send({ type: 'ready' });
      }
    } else {
      startLocalGame();
    }
  },
  onDir(dir: Direction) {
    if (game.online && game.onlineRole === Role.PLAYER) {
      send({ type: 'dir', dir });
    }
  },
  onToggleNavOverlay() {
    toggleNavOverlay();
  },
  onJump() {
    if (game.online) {
      send({ type: 'jump' });
    } else {
      game.jumpPending = true;
    }
  },
});

// --- Resize ---
window.addEventListener('resize', () => {
  if (game.state !== ScreenState.PLAYING && !game.online) {
    sizeCanvas();
    initLocalGame();
    draw();
  }
});

// --- Boot ---
sizeCanvas();
initLocalGame();
draw();
setScreen(ScreenState.HOME);
if (isFirstHomeLoad && !savedName) {
  els.nameInput.focus();
  isFirstHomeLoad = false;
}
connectStatus(updateOnlineStatus);
